<script>
import DesktopHeader from '@/components/header/DesktopHeader';
import TopHeader from '@/components/header/TopHeader';
import MobileHeader from '@/components/header/MobileHeader';
import { useTestAssortmentMenu } from '@/composables/useTestAssortmentMenu';
import { hasHeroOverlayKey } from '@/keys';

export default {
  components: {
    DesktopHeader,
    MobileHeader,
    TopHeader,
  },
  inject: {
    testAssortmentHomeSessionValue: 'testAssortmentHomeSessionValue',
    hasHeroOverlay: { from: hasHeroOverlayKey, default: false },
  },
  computed: {
    header() {
      return this.$header;
    },
    pageHasHeroOverlay() {
      const blocks = this.$page?.blocks;
      if (!blocks) return false;
      return Object.values(blocks).some(
        (container) =>
          Array.isArray(container) &&
          container.some((b) => b?.template === 'GetMyGiftBlock'),
      );
    },
    isInverted() {
      // Derive from page data (SSR-safe, no flash on first load), with the
      // injected ref as a reactive fallback for client-side block changes.
      return this.pageHasHeroOverlay || this.hasHeroOverlay === true;
    },
    linkList() {
      if (useTestAssortmentMenu(this.$activeGiftCard, this.testAssortmentHomeSessionValue)) {
        return this.header?.linkListTestAssortment || this.header?.fields?.LinkList || [];
      }
      return this.header?.fields?.LinkList || [];
    },
    logoType() {
      return this.$channel.headerLogotype || '';
    },
    logoTypeVersion() {
      return this.$channel.headerLogotypeVersion || '';
    },
    logoTypeInverted() {
      return this.$channel.headerLogotypeInverted || '';
    },
    logoTypeInvertedVersion() {
      return this.$channel.headerLogotypeInvertedVersion || '';
    },
    rootPath() {
      return this.$channel.rootPath || '';
    },
    getCategoryUrl() {
      if (useTestAssortmentMenu(this.$activeGiftCard, this.testAssortmentHomeSessionValue)) {
        return this.testAssortmentHomeSessionValue || '';
      }
      return this.$page.dataJson?.dataJson?.giftCardCategory?.url || this.$page.dataJson?.dataJson?.category?.url || '';
    },
    rootPathRedirectUrl() {
      return this.getCategoryUrl || this.$channel.rootPathRedirectUrl || '';
    },
    logoTypeUrl() {
      if (!this.logoType) return '';
      let url = this.$toLitiumMediaUrl(this.logoType);
      if (this.logoTypeVersion) url += '?v=' + this.logoTypeVersion;
      return url;
    },
    logoTypeInvertedUrl() {
      if (!this.logoTypeInverted) return this.logoTypeUrl;
      let url = this.$toLitiumMediaUrl(this.logoTypeInverted);
      if (this.logoTypeInvertedVersion) url += '?v=' + this.logoTypeInvertedVersion;
      return url;
    },
    currentLogoUrl() {
      return this.isInverted ? this.logoTypeInvertedUrl : this.logoTypeUrl;
    },
    links() {
      if (!this.linkList) {
        return [];
      }
      return this.linkList
        .filter((l) => l.url && l.fields._name)
        .map((l) => ({
          caption: l.fields._name,
          url: this.isRootPath(l.url) ? this.rootPathRedirectUrl : l.url,
        }));
    },
  },
  methods: {
    isRootPath(path) {
      const rootPath = this.$channel.rootPath || '';
      return rootPath === path || rootPath === (path + '/');
    },
  },
};
</script>

<template>
  <div
    class="site-header"
    :class="isInverted
      ? 'absolute inset-x-0 top-0 z-[900] !bg-transparent'
      : 'contents'"
  >
    <top-header class="site-header__top" :inverted="isInverted" />
    <div
      class="site-header__desktop"
      :class="!isInverted && 'sticky top-0 z-[900] w-full border-b border-border bg-background'"
    >
      <desktop-header :logoUrl="currentLogoUrl" :inverted="isInverted" />
    </div>
    <mobile-header
      class="site-header__mobile"
      :class="!isInverted && 'sticky top-0 z-[900] border-b border-border bg-background'"
      :logoUrl="currentLogoUrl"
      :menuLogoUrl="logoTypeUrl"
      :links="links"
      :inverted="isInverted"
    />
  </div>
</template>

<style>
.site-header {
  background-color: var(--main-background-color);
}

@media (--phoneAndTablet) {
  .site-header__desktop {
    display: none;
  }
}

@media (--desktop) {
  .site-header__mobile {
    display: none;
  }
}
</style>
